import React, { Fragment, useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import moment from 'moment'
import MasterTable from './MasterTable'
import AdminNavbar from './AdminNavbar'
import Pager from './Pager'
import StatusBtns from './StatusBtns'
import ClientManager from './ClientManager'
import AdminDueDiligence from './AdminDueDiligence'
import Reports from './Reports'
import Billing from './Billing'
import Partners from './Partners'
import PartnerClientManager from './PartnerClientManager'
import {
  watchClients,
  watchLeadNotes,
  watchLeads,
  addLead,
  watchPartnerClients,
  watchPartnerLeads,
  watchPartnerLeadNotes,
} from '../fire'
import { rowsPerPage, statusList } from '../constants'
import { justDigits } from '../utils'
import PartnerManuallyAddLeadBtn from './PartnerManuallyAddLeadBtn'

let Admin = ({ setLoggedIn, userConfig }) => {
  const { adminPermissions } = userConfig
  const { fullAccess, limitedAccess, partnerAccess } = adminPermissions
  const [searchVal, setSearchVal] = useState('')
  const [month, setMonth] = useState(moment().format('YYYY-MM'))
  const [page, setPage] = useState(0)
  const [showFlatFeeLeads, setShowFlatFeeLeads] = useState(false)
  const [sort, setSort] = useState({ field: 'date', order: 'desc' })
  const [clients, setClients] = useState([])
  const [leadsOnly, setLeads] = useState([])
  const [leadNotes, setNotes] = useState({})
  const [shownStatuses, setShownStatuses] = useState(statusList)
  const [view, setView] = useState('leads') // 'billing', 'partners', 'clients', 'reports', 'dueDiligence', or 'leads'
  const leads = _.map(leadsOnly, l => ({
    ...l,
    billingType: _.chain(clients)
      .find({ id: l.clientId })
      .get('billingType', null)
      .value(),
  }))

  let partnerLeadsObject = useRef({
    // 'client-id': [...]
  }) // store leads by client id to help remove leads from table when they're deleted, rather than just concat new stuff
  useEffect(() => {
    let {
      adminPermissions: { partnerAccess },
      partnerClientIds,
    } = userConfig
    let leadsOff = partnerAccess
      ? watchPartnerLeads(
          { month, partnerClientIds },
          ({ clientId, clientLeads }) => {
            partnerLeadsObject.current[clientId] = clientLeads
            setLeads(_.flatten(_.values(partnerLeadsObject.current)))
          }
        )
      : watchLeads(month, l => setLeads(l))
    let leadNotesOff = partnerAccess
      ? watchPartnerLeadNotes({ month, partnerClientIds }, clientLeadNotes =>
          setNotes(prev => ({ ...prev, ...clientLeadNotes }))
        )
      : watchLeadNotes(month, n => setNotes(n))
    let clientsOff = partnerAccess
      ? watchPartnerClients({ partnerClientIds }, client =>
          setClients(prev => _.uniqBy([client, ...prev], 'id'))
        )
      : watchClients(c => setClients(c))

    return () => {
      leadsOff()
      leadNotesOff()
      clientsOff()
    }
  }, [month, userConfig])

  useEffect(() => {
    setSearchVal('')
  }, [view])

  const filteredLeads = _(leads)
    .filter(l => {
      let digitOnly = justDigits(searchVal)
      let lowerSearchVal = _.toLower(searchVal)
      let statusAndSearchFiltersOk =
        (_.includes(shownStatuses, l.status) ||
          shownStatuses.length === statusList.length) &&
        (_.includes(_.toLower(l.name), lowerSearchVal) ||
          _.includes(_.toLower(l.address), lowerSearchVal) ||
          _.includes(_.toLower(l.city), lowerSearchVal) ||
          _.includes(_.toLower(l.email), lowerSearchVal) ||
          _.includes(_.toLower(l.clientId), lowerSearchVal) ||
          (digitOnly && _.includes(justDigits(l.phoneNumber), digitOnly)))

      return (
        statusAndSearchFiltersOk &&
        (!showFlatFeeLeads || (showFlatFeeLeads && l.billingType !== 'perLead'))
      )
    })
    .value()

  return (
    <div className="container-fluid px-0">
      <AdminNavbar
        {...{
          view,
          setView,
          searchVal,
          setSearchVal,
          month,
          setMonth,
          setPage,
          setLoggedIn,
          adminPermissions,
        }}
      />
      <div className="p-2">
        {view === 'leads' && (
          <Fragment>
            <div className="my-2 d-flex">
              <StatusBtns
                leads={leads}
                shownStatuses={shownStatuses}
                setShownStatuses={setShownStatuses}
              />
              <button
                className="btn btn-sm btn-warning ml-5"
                onClick={() => setShowFlatFeeLeads(!showFlatFeeLeads)}
              >
                <i
                  className={`fa fa-${showFlatFeeLeads ? 'check' : 'remove'}`}
                />{' '}
                Show Flat Fee Only
                <span className="badge">
                  {_.filter(leads, l => l.billingType !== 'perLead').length}
                </span>
              </button>
              {(fullAccess || limitedAccess) && (
                <button
                  className="btn btn-secondary btn-sm ml-auto"
                  onClick={_.throttle(() => addLead({ month }), 2000, {
                    trailing: false,
                  })}
                >
                  <i className="fa fa-plus" /> Manually Add Lead
                </button>
              )}
              {partnerAccess && (
                <PartnerManuallyAddLeadBtn
                  month={month}
                  partnerClientIds={userConfig.partnerClientIds}
                />
              )}
            </div>
            <MasterTable
              editClientIdAllowed
              {...{
                clients,
                page,
                leads: filteredLeads,
                setLeads,
                leadNotes,
                sort,
                setSort,
                month,
                searchVal,
                adminPermissions,
                partnerClientIds: userConfig.partnerClientIds,
              }}
            />
            <Pager
              page={page}
              pageCount={Math.ceil(filteredLeads.length / rowsPerPage)}
              setPage={setPage}
            />
          </Fragment>
        )}
        {view === 'clients' && (fullAccess || limitedAccess) && (
          <>
            <ClientManager
              clients={clients}
              page={page}
              setPage={setPage}
              searchVal={searchVal}
              sort={sort}
              setSort={setSort}
            />
          </>
        )}
        {view === 'clients' && partnerAccess && (
          <PartnerClientManager
            clients={clients}
            page={page}
            setPage={setPage}
            searchVal={searchVal}
            sort={sort}
            setSort={setSort}
          />
        )}
        {view === 'reports' && (
          <Reports showEmailLeads {...{ clients, filteredLeads }} />
        )}
        {view === 'billing' && fullAccess && <Billing />}
        {view === 'dueDiligence' && fullAccess && <AdminDueDiligence />}
        {view === 'partners' && fullAccess && <Partners clients={clients} />}
      </div>
    </div>
  )
}

export default Admin
